import { useState, useRef, useEffect, useMemo, type JSX, type CSSProperties } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, X } from 'lucide-react';
import clsx from 'clsx';

interface ArbiDatePickerProps {
  value: string | null;
  onChange: (next: string | null) => void;
  /** ISO YYYY-MM-DD; selecting a date earlier than this is disabled. */
  min?: string;
  /** ISO YYYY-MM-DD; selecting a date later than this is disabled. */
  max?: string;
  placeholder?: string;
  size?: 'sm' | 'xs';
  disabled?: boolean;
  className?: string;
  /** When true, renders a small × button to clear the value (no-op when value is null). */
  clearable?: boolean;
}

/**
 * ArbiDatePicker — Arbi custom date picker. Replaces browser-default
 * <input type="date"> per UI_STANDARDS §13. Sibling primitive to ArbiSelect /
 * ArbiToggle. Hand-rolled calendar grid; no external date library.
 */
export default function ArbiDatePicker({
  value,
  onChange,
  min,
  max,
  placeholder = 'mm/dd/yyyy',
  size = 'sm',
  disabled = false,
  className,
  clearable = false,
}: ArbiDatePickerProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const selected = useMemo(() => parseISO(value), [value]);
  const minDate = useMemo(() => parseISO(min ?? null), [min]);
  const maxDate = useMemo(() => parseISO(max ?? null), [max]);

  const today = useMemo(() => stripTime(new Date()), []);

  // Calendar focus month — defaults to selected date's month, today otherwise.
  const [viewYear, setViewYear] = useState<number>((selected ?? today).getFullYear());
  const [viewMonth, setViewMonth] = useState<number>((selected ?? today).getMonth());

  // Realign the view month when value changes (e.g. parent wires a new default).
  useEffect(() => {
    if (selected) {
      setViewYear(selected.getFullYear());
      setViewMonth(selected.getMonth());
    }
  }, [selected]);

  // Outside click closes
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      const target = e.target as Node | null;
      if (wrapperRef.current && !wrapperRef.current.contains(target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Escape closes
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const sizeClasses = size === 'xs' ? 'text-[11px] px-2 py-1 gap-1' : 'text-xs px-3 py-1.5 gap-1.5';

  const displayLabel = selected ? selected.toLocaleDateString() : placeholder;

  const goPrevMonth = (): void => {
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else {
      setViewMonth((m) => m - 1);
    }
  };
  const goNextMonth = (): void => {
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const handlePick = (d: Date): void => {
    onChange(formatISO(d));
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onChange(null);
  };

  const cells = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  const popoverStyle: CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    zIndex: 100,
    marginTop: 2,
  };

  return (
    <div ref={wrapperRef} className={clsx('relative inline-block', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setOpen((v) => !v);
        }}
        className={clsx(
          'flex items-center justify-between rounded-md',
          'bg-surface-3 border border-border transition-colors',
          'text-left select-none w-full',
          open && 'border-accent',
          disabled && 'opacity-50 cursor-not-allowed',
          sizeClasses
        )}
      >
        <span className={clsx('font-mono', selected ? 'text-ink-primary' : 'text-ink-muted')}>
          {displayLabel}
        </span>
        <span className="flex items-center gap-1 text-ink-muted">
          {clearable && selected && (
            <X
              size={size === 'xs' ? 10 : 12}
              onClick={handleClear}
              className="hover:text-ink-primary transition-colors"
            />
          )}
          <CalendarIcon size={size === 'xs' ? 10 : 12} />
        </span>
      </button>

      {open && (
        <div
          className="bg-surface-2 border border-border rounded-lg shadow-xl p-3"
          style={popoverStyle}
        >
          {/* Header — prev / month label / next */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={goPrevMonth}
              className="p-1 rounded hover:bg-surface-3 text-ink-secondary hover:text-ink-primary transition-colors"
              aria-label="Previous month"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs font-medium text-ink-bright select-none">{monthLabel}</span>
            <button
              type="button"
              onClick={goNextMonth}
              className="p-1 rounded hover:bg-surface-3 text-ink-secondary hover:text-ink-primary transition-colors"
              aria-label="Next month"
            >
              <ChevronRight size={14} />
            </button>
          </div>

          {/* Day-of-week row */}
          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
              <span
                key={`${d}-${i}`}
                className="text-2xs uppercase tracking-wider text-ink-muted text-center py-1"
              >
                {d}
              </span>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((cell, i) => {
              const inMonth = cell.getMonth() === viewMonth;
              const isToday = isSameDay(cell, today);
              const isSelected = selected ? isSameDay(cell, selected) : false;
              const isOutOfRange = (minDate && cell < minDate) || (maxDate && cell > maxDate);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => !isOutOfRange && handlePick(cell)}
                  disabled={!!isOutOfRange}
                  className={clsx(
                    'text-xs font-mono w-7 h-7 rounded transition-colors flex items-center justify-center',
                    isSelected && 'bg-accent text-white',
                    !isSelected && isToday && 'border border-accent text-accent',
                    !isSelected && !isToday && inMonth && 'text-ink-primary hover:bg-surface-3',
                    !isSelected && !inMonth && 'text-ink-muted hover:bg-surface-3',
                    isOutOfRange && 'opacity-30 cursor-not-allowed hover:bg-transparent'
                  )}
                >
                  {cell.getDate()}
                </button>
              );
            })}
          </div>

          {/* Today shortcut */}
          <div className="mt-2 pt-2 border-t border-border flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                const t = new Date();
                if ((minDate && t < minDate) || (maxDate && t > maxDate)) return;
                handlePick(t);
              }}
              className="text-2xs text-accent hover:text-accent-hover transition-colors"
            >
              Today
            </button>
            {clearable && selected && (
              <button
                type="button"
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                }}
                className="text-2xs text-ink-muted hover:text-ink-primary transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function parseISO(s: string | null | undefined): Date | null {
  if (!s) return null;
  // Treat as a calendar date in local time — never UTC. Otherwise the
  // displayed date can drift by a day depending on timezone.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? null : d;
}

function formatISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function stripTime(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Build a 6-row (42-cell) grid covering the given month plus leading days
 * from the previous month and trailing days from the next month so the calendar
 * always renders a uniform shape.
 */
function buildMonthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const offset = first.getDay(); // 0 = Sun
  const start = new Date(year, month, 1 - offset);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    cells.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }
  return cells;
}
